import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminLogin } from '../../lib/api';

export function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Focus the email field on mount — equivalent to `autoFocus`, without the
  // JSX attribute the linter flags.
  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await adminLogin(email, password);
      navigate('/admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <motion.div
        className="max-w-sm w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="text-center mb-8">
          <h1 className="font-serif text-2xl font-bold text-text">Admin Dashboard</h1>
          <p className="text-text-light text-sm mt-1">Sign in to manage your store</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl border border-sage/10 p-8 shadow-sm space-y-5"
        >
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-text mb-1.5">
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              required
              ref={emailInputRef}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-sage/30 bg-white text-text focus:outline-none focus:ring-2 focus:ring-sage/50 transition"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-sm font-medium text-text mb-1.5">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-sage/30 bg-white text-text focus:outline-none focus:ring-2 focus:ring-sage/50 transition"
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-2.5 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-2.5 rounded-lg bg-sage text-white font-semibold hover:bg-sage-dark disabled:opacity-60 transition-colors"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
